
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2, Eye, EyeOff, Download, Image, Copy } from "lucide-react";

interface PrintableItem {
  id: string;
  category_id: string | null;
  title: string;
  description: string | null;
  zip_url: string;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

interface PrintableItemsTableProps {
  items: PrintableItem[];
  loading: boolean;
  selectedCategory: string;
  getCategoryName: (categoryId: string | null) => string;
  onEdit: (item: PrintableItem) => void;
  onDuplicate: (item: PrintableItem) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, currentStatus: boolean) => void;
}

const PrintableItemsTable = ({
  items,
  loading,
  selectedCategory,
  getCategoryName,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
}: PrintableItemsTableProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Items List ({items.length} items)
          {selectedCategory !== "all" && (
            <span className="text-sm font-normal text-slate-600 ml-2">
              - {getCategoryName(selectedCategory)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>ZIP URL</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                          <Image className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell>{getCategoryName(item.category_id)}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.description || 'No description'}</TableCell>
                    <TableCell>
                      <a
                        href={item.zip_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${item.is_free ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {item.is_free ? 'Free' : 'Premium'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={item.is_active ? "default" : "secondary"}
                        onClick={() => onToggleActive(item.id, item.is_active)}
                        className="w-20"
                      >
                        {item.is_active ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>{item.display_order}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEdit(item)}
                          title="Edit item"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDuplicate(item)}
                          title="Duplicate item"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onDelete(item.id)}
                          title="Delete item"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      {selectedCategory === "all" ? "No items found" : `No items found in ${getCategoryName(selectedCategory)} category`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrintableItemsTable;
